import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sprout, TrendingUp, Bot, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LOGO_URL } from "../lib/brand";
import { isLocked, subscribe } from "../lib/scrollLock";
import useBodyScrollLock from "../hooks/useBodyScrollLock";

const NAV_EVERY = 4;          // reopen after this many page navigations
const INTERVAL_MS = 180000;   // check every 3 minutes
const FIRST_DELAY_MS = 20000; // first appearance after 20s of browsing
const MIN_GAP_MS = 180000;    // minimum 3 min between two shows
const MAX_SHOWS = 3;          // never nag more than 3 times per session

export default function GuestSignupNudge() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const navCount = useRef(0);
  const shows = useRef(0);
  const lastShow = useRef(0);
  const pending = useRef(false);
  const isDemo = !!user?.isDemo;

  useBodyScrollLock(open);

  const tryShow = useCallback(() => {
    if (open || shows.current >= MAX_SHOWS) return;
    if (Date.now() - lastShow.current < MIN_GAP_MS && shows.current > 0) return;
    if (isLocked()) { pending.current = true; return; }   // never over an open sheet/modal
    pending.current = false;
    shows.current += 1;
    lastShow.current = Date.now();
    setOpen(true);
  }, [open]);

  // first load + periodic
  useEffect(() => {
    if (!isDemo) return undefined;
    const first = setTimeout(tryShow, FIRST_DELAY_MS);
    const iv = setInterval(tryShow, INTERVAL_MS);
    return () => { clearTimeout(first); clearInterval(iv); };
  }, [isDemo, tryShow]);

  // flush a queued nudge once every overlay has closed
  useEffect(() => {
    if (!isDemo) return undefined;
    return subscribe((locks) => { if (locks === 0 && pending.current) setTimeout(tryShow, 500); });
  }, [isDemo, tryShow]);

  // reopen after N navigations
  useEffect(() => {
    if (!isDemo) return;
    navCount.current += 1;
    if (navCount.current > 0 && navCount.current % NAV_EVERY === 0) tryShow();
  }, [location.pathname, isDemo, tryShow]);

  if (!isDemo) return null;

  const dismiss = () => setOpen(false);
  const go = async () => { setOpen(false); try { await logout(); } catch {} navigate("/login"); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[10800] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={dismiss} data-testid="guest-nudge">
          <motion.div onClick={(e) => e.stopPropagation()}
            initial={{ y: 40, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="w-full max-w-md rounded-3xl bg-card border shadow-2xl p-6 relative">
            <button onClick={dismiss} data-testid="guest-nudge-close" aria-label="Close" className="absolute top-4 right-4 text-muted-foreground hover:text-foreground z-10"><X className="h-5 w-5" /></button>
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="Agro Madat" className="h-11 w-11 rounded-xl object-contain bg-white shadow-sm" />
              <div><p className="font-bold leading-tight">You're in the demo farm</p><p className="text-xs text-muted-foreground">Create your free account to make it yours</p></div>
            </div>
            <div className="space-y-2.5 mb-5">
              {[[Sprout, "Add your own fields & crops"], [TrendingUp, "Track real finances & mandi prices"], [Bot, "Save AI advice for your farm"]].map(([Ic, txt], i) => (
                <div key={i} className="flex items-center gap-2.5 text-sm"><span className="h-7 w-7 rounded-lg bg-green-50 flex items-center justify-center shrink-0"><Ic className="h-4 w-4 text-green-600" /></span>{txt}</div>
              ))}
            </div>
            <button onClick={go} data-testid="guest-nudge-signup" className="w-full py-3.5 rounded-2xl bg-green-600 text-white font-semibold hover:bg-green-700 tap active:scale-[0.98] transition-transform flex items-center justify-center gap-2">
              <ShieldCheck className="h-4 w-4" />Sign up free — takes 60 seconds
            </button>
            <button onClick={dismiss} data-testid="guest-nudge-later" className="w-full mt-2 py-2.5 text-sm text-muted-foreground hover:text-foreground">Keep exploring the demo</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
